import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Avatar,
  InputAdornment,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

export default function SearchBar({ onResults }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch suggestions as user types
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.trim() !== "") {
        fetchSuggestions(query);
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query]);

  const fetchSuggestions = async (searchText) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/list_posted_data1/`, {
        params: { search: searchText, limit: 5, page: 1 },
      });

      const mapped = res.data.map((item) => ({
        id: item.id,
        title: item.highlightedTopic,
        teacher: item.teacher,
      }));

      setSuggestions(mapped);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleEnter = async (e) => {
    if (e.key === "Enter") {
      try {
        const res = await axios.get(`${API_URL}/list_posted_data1/`, {
          params: { search: query, limit: 20, page: 1 },
        });

        onResults(res.data);
        setSuggestions([]);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleSelect = async (item) => {
    setQuery(item.title);
    setSuggestions([]);

    try {
      const res = await axios.get(`${API_URL}/list_posted_data1/`, {
        params: { search: query, limit: 20, page: 1 },
      });

      const selectedPost = res.data.find((p) => p.id === item.id);
      const relatedPosts = res.data.filter((p) => p.id !== item.id);

      const combined = [];
      if (selectedPost) combined.push(selectedPost);
      combined.push(...relatedPosts);

      onResults(combined);
    } catch (err) {
      console.error(err);
    }
  };

  return (
   <Box
  sx={{
    width: "100%",
    maxWidth: {
      xs: "95%",   // mobile: almost full width
      sm: 600,     // tablet
      md: 750,     // laptop
      lg: 700,     // desktop
      xl: 1200,    // large desktop
    },
    mx: "auto",
    position: "relative",
    // zIndex: 2000,
    zIndex: {
      xs: "auto", // or 0
      lg: 2000,   // only large screens and up
    },
    mt: {
      xs: 1,
      sm: 8,
      md: 8,
      lg: 1,
    },
  }}
>



      <TextField
        fullWidth
        placeholder="Search sessions..."
        variant="outlined"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleEnter}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#aaa" }} />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <CircularProgress size={20} sx={{ ml: 1, color: "#80caff" }} />
          ) : null,
          sx: {
            borderRadius: 3,
            backgroundColor: "#1f1f1f",
            color: "#fff",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#333",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#555",
            },
            "& .MuiInputBase-input": {
              color: "#fff",
            },
            "& .MuiSvgIcon-root": {
              color: "#aaa",
            },
          },
        }}
      />

      {suggestions.length > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            width: "100%",
            zIndex: 9999,
            mt: 1,
            borderRadius: 2,
            overflow: "hidden",
            transition: "all 0.3s ease",
            boxShadow: "0 8px 20px rgba(0,0,0,0.5)",
            bgcolor: "#000000ff",
            maxHeight: 300,
          }}
        >
          <List>
            {suggestions.map((s) => (
              <ListItem
                button
                key={s.id}
                onClick={() => handleSelect(s)}
                sx={{
                  px: 2,
                  py: 1,
                  transition: "0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #000000ff, #0c0c0cff)",
                  },
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                }}
              >
                <Avatar
                  sx={{
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 },
                    bgcolor: "#339af0",
                    width: 36,
                    height: 36,
                  }}
                >
                  {s.teacher ? s.teacher.charAt(0).toUpperCase() : "T"}
                </Avatar>
                <ListItemText
                  primary={
                    <Typography fontWeight={600} color="#fff">
                      {s.title}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="#aaa">
                      By {s.teacher || "Unknown"}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
